import AhoCorasick from "modern-ahocorasick";
import type { Hint } from "~/features/chat/types";

export interface TermMatch {
  start: number;
  end: number;
  term: string;
}

/**
 * Aho-Corasick based term matcher for large dictionaries (e.g. medications.json).
 * Scans text in O(n + z) time regardless of pattern count.
 *
 * Note: modern-ahocorasick uses Intl.Segmenter (grapheme granularity) internally.
 * For ASCII text (which medications and medical reports are), grapheme indices
 * equal character indices, so we use them directly. If non-ASCII multi-codepoint
 * grapheme clusters appear before matches, positions could diverge — but this
 * does not occur in our medical term/report content.
 */
export class TermMatcher {
  private ac: AhoCorasick;
  private termLengths: Map<string, number>;

  constructor(terms: string[]) {
    const lowered = terms.map((t) => t.toLowerCase());
    this.ac = new AhoCorasick(lowered);
    this.termLengths = new Map(lowered.map((t) => [t, t.length]));
  }

  /**
   * Returns all non-overlapping matches sorted by position.
   * Longer matches take priority when overlapping.
   */
  search(text: string): TermMatch[] {
    const lower = text.toLowerCase();
    const raw = this.ac.search(lower);

    // raw format: [endIndex, [matchedKeywords]]
    const allMatches: TermMatch[] = [];
    for (const [endIdx, keywords] of raw) {
      for (const keyword of keywords) {
        const len = this.termLengths.get(keyword) ?? keyword.length;
        const start = endIdx - len + 1;
        const end = endIdx + 1;

        // Word boundary check: ensure match isn't mid-word
        if (start > 0 && isWordChar(lower.charCodeAt(start - 1))) continue;
        if (end < lower.length && isWordChar(lower.charCodeAt(end))) continue;

        allMatches.push({ start, end, term: keyword });
      }
    }

    // Sort by start position, then prefer longer matches
    allMatches.sort((a, b) => a.start - b.start || b.end - a.end);

    // Remove overlapping matches (greedy: keep longer/earlier)
    const result: TermMatch[] = [];
    let lastEnd = 0;
    for (const m of allMatches) {
      if (m.start >= lastEnd) {
        result.push(m);
        lastEnd = m.end;
      }
    }

    return result;
  }
}

function isWordChar(charCode: number): boolean {
  return (
    (charCode >= 48 && charCode <= 57) || // 0-9
    (charCode >= 65 && charCode <= 90) || // A-Z
    (charCode >= 97 && charCode <= 122) || // a-z
    charCode === 95 // _
  );
}

// Lazy singleton for medications
let medicationMatcher: TermMatcher | null = null;
let medicationTerms: Hint[] = [];

/**
 * Builds or returns the cached medication matcher.
 * Only rebuilds if medications array reference changes.
 */
export function getMedicationMatcher(medications: Hint[]): TermMatcher | null {
  if (!medications.length) return null;
  if (medications === medicationTerms && medicationMatcher) {
    return medicationMatcher;
  }
  medicationTerms = medications;
  medicationMatcher = new TermMatcher(medications.map((m) => m.term));
  return medicationMatcher;
}
